import { useRef } from 'react';
import { 
  Lightning, 
  Star, 
  Package, 
  Heart, 
  TrendUp, 
  Trophy, 
  Leaf, 
  Crown,
  Sparkle,
  Storefront
} from '@phosphor-icons/react';
import { InstantSearch, Index, Configure, Hits } from 'react-instantsearch';
import { CarouselProductHit } from '../Product/CarouselProductHit';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../context/StoreContext';
import { searchClient } from '../../services/algoliaClient';

// Carousel Section Component
const CarouselSection = ({ title, Icon, indexId, hitsPerPage = 6, additionalFilter = '' }) => {
  const carouselRef = useRef(null);
  const { selectedStore } = useStore();
  const { t } = useTranslation();

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const container = carouselRef.current;
      const containerWidth = container.offsetWidth;
      
      // Determine card width based on screen size
      let cardWidth, gap;
      
      if (containerWidth <= 480) {
        // Small mobile: 190px card width
        cardWidth = 190;
        gap = 6; // 0.4rem = 6px
      } else if (containerWidth <= 768) {
        // Tablet: 220px card width
        cardWidth = 220;
        gap = 8; // 0.5rem = 8px
      } else {
        // Desktop: 280px card width
        cardWidth = 280;
        gap = 12; // 0.75rem = 12px
      }
      
      const totalCardWidth = cardWidth + gap;
      
      // Calculate how many cards to show based on container width
      let cardsToShow = Math.floor(containerWidth / totalCardWidth);
      
      // Ensure at least 1 card is shown, and on small mobile, always show 1
      if (containerWidth <= 480) {
        cardsToShow = 1;
      } else {
        cardsToShow = Math.max(1, cardsToShow);
      }
      
      // Calculate scroll amount based on visible cards
      const scrollAmount = cardsToShow * totalCardWidth;
      
      const newScrollLeft = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Build the filter string combining store filter and additional filter
  const storeFilter = selectedStore ? `attributes.store_availability_list:${selectedStore}` : '';
  let finalFilter = '';
  
  if (storeFilter && additionalFilter) {
    finalFilter = `${storeFilter} AND ${additionalFilter}`;
  } else if (storeFilter) {
    finalFilter = storeFilter;
  } else if (additionalFilter) {
    finalFilter = additionalFilter;
  }

  // Create analytics tags and rule contexts
  const analyticsTags = [indexId];
  const ruleContexts = selectedStore ? [`${indexId}-${selectedStore}`] : [indexId];

  return (
    <section className="carousel-section">
      <h2>
        <Icon size={24} />
        {title}
      </h2>
      
      <div className={`carousel-container ${!selectedStore ? 'carousel-card-with-overlay' : ''}`}>
        <button 
          className="carousel-button left" 
          onClick={() => scrollCarousel('left')}
          aria-label="Scroll left"
        >
          ‹
        </button>
        
        <div className="carousel" ref={carouselRef}>
          <Index indexName="saq_products" indexId={indexId}>
            <Configure 
              query=""
              hitsPerPage={hitsPerPage}
              filters={finalFilter}
              analyticsTags={analyticsTags}
              ruleContexts={ruleContexts}
            />
            <Hits 
              hitComponent={CarouselProductHit}
              className="ais-Hits-list"
            />
          </Index>
        </div>
        
        <button 
          className="carousel-button right" 
          onClick={() => scrollCarousel('right')}
          aria-label="Scroll right"
        >
          ›
        </button>

        {!selectedStore && (
          <div className="carousel-overlay">
            <div className="carousel-overlay-content">
              <Storefront className="carousel-overlay-icon" />
              <div className="carousel-overlay-title">
                {t('select_store_title', 'Sélectionnez une succursale')}
              </div>
              <div className="carousel-overlay-text">
                {t('select_store_message', 'Choisissez votre succursale pour voir les recommandations personnalisées')}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Home Page with single InstantSearch component
export const Home = () => {
  const { t } = useTranslation();

  return (
    <main className="main-content">
      <div className="hero-section">
        <div className="hero-content">
          <h1>{t('discover_wines')}</h1>
          <p>Découvrez notre sélection exceptionnelle de vins, cépages et régions dans votre succursale</p>
        </div>
      </div>
      
      <InstantSearch 
        searchClient={searchClient} 
        indexName="saq_products"
        future={{
          preserveSharedStateOnUnmount: false
        }}
      >
        {/* New Arrivals */}
        <CarouselSection
          title={t('new_arrivals')}
          Icon={Lightning}
          indexId="new_arrivals"
          hitsPerPage={6}
        />

        {/* Coups de Cœur */}
        <CarouselSection
          title={t('coups_de_coeur')}
          Icon={Heart}
          indexId="coups_de_coeur"
          hitsPerPage={6}
        />

        {/* Cellar */}
        <CarouselSection
          title={t('cellar')}
          Icon={TrendUp}
          indexId="cellar"
          hitsPerPage={6}
          additionalFilter="attributes.gamme_marketing:'Produit Cellier'"
        />

        {/* Last Chances */}
        <CarouselSection
          title={t('last_chances')}
          Icon={Crown}
          indexId="last_chances"
          hitsPerPage={6}
        />
      </InstantSearch>
    </main>
  );
}; 